import { getSupabaseClient } from "@/core/supabase/client.js";
import { fetchAllPaged } from "@/core/supabase/pagination.js";
import type {
  ProjectReportPageDto,
  ReportFormxSubmissionCountRowDto,
  ReportFormxSubmissionRowDto,
  ReportKpiValueRowDto,
  ReportSessionRowDto,
} from "@/features/project/data/dtos/ProjectReportDto.js";
import type { ProjectReportPageFilters } from "@/features/project/domain/entities/ProjectReportRowEntity.js";

const FORMX_DETAIL_FIELDS =
  'id, session_id, form_id, submitted_at, form:formx_forms!form_id(name, sort_order), values:formx_values(value, field:formx_fields!field_id(field_type, label, sort_order))';

const SESSION_FIELDS = `
  id, achiever_id, location_id, started_at, ended_at, scheduled_start_at, scheduled_end_at,
  start_photo_urls, end_photo_urls,
  achiever_profile:profiles!achiever_id(id, first_name, last_name, email),
  location:locations!sessions_location_id_fkey(
    id, name, code, categories, province_code, ward_code, address_line, full_address_cache,
    province:locations_provinces(code, name, full_name),
    ward:locations_wards(code, name, full_name)
  )
`;

export class ProjectReportRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  /**
   * Bounded report-table data. Unlike the legacy full report reads below,
   * this never downloads project history merely to display one page.
   */
  async getProjectReportPage(
    orgId: string,
    projectId: string,
    filters: ProjectReportPageFilters,
  ): Promise<ProjectReportPageDto> {
    const { data, error } = await this.supabase.rpc('get_project_report_page', {
      p_org_id: orgId,
      p_project_id: projectId,
      p_started_from: filters.startedFrom ?? null,
      p_started_before: filters.startedBefore ?? null,
      p_status: filters.status ?? null,
      p_team: filters.team ?? null,
      p_achiever_id: filters.achieverId ?? null,
      p_location_id: filters.locationId ?? null,
      p_form_id: filters.formId ?? null,
      p_limit: filters.limit,
      p_offset: filters.offset,
      p_sort_by: filters.sortBy ?? 'status',
      p_sort_direction: filters.sortDirection ?? 'asc',
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as ProjectReportPageDto;
  }

  /**
   * Sessions of a project, with the achiever profile joined for display.
   * PostgREST silently caps un-paginated selects at `max_rows` (1000 locally)
   * — fetchAllPaged loops `.range()` so a project past that cap doesn't
   * quietly lose sessions off the end. See core/supabase/pagination.ts.
   */
  async listSessions(orgId: string, projectId: string): Promise<ReportSessionRowDto[]> {
    return fetchAllPaged<ReportSessionRowDto>((from, to) =>
      this.supabase
        .from('sessions')
        .select(SESSION_FIELDS)
        .eq('org_id', orgId)
        .eq('project_id', projectId)
        .order('started_at', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false })
        .range(from, to),
    );
  }

  /** Every kpi_values row in the project (with its KPI joined), session-scoped. Paginated — see listSessions. */
  async listKpiValues(orgId: string, projectId: string): Promise<ReportKpiValueRowDto[]> {
    return fetchAllPaged<ReportKpiValueRowDto>((from, to) =>
      this.supabase
        .from('kpi_values')
        .select('id, session_id, kpi_id, submission_id, manual_value, system_value, created_at, kpi:kpis!kpi_id(name, unit)')
        .eq('org_id', orgId)
        .eq('project_id', projectId)
        .not('session_id', 'is', null)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
  }

  /**
   * Submission count per session, project-wide — id + session_id only, no
   * values/fields embed. Cheap: skips the formx_values RLS fanout entirely.
   * Powers the table's "Biểu mẫu nộp" count column. Paginated — see listSessions
   * (this one had no `.order()` at all before, so past 1000 rows it wasn't
   * just truncated but truncated in an *unspecified* order — some sessions'
   * counts would come out right and others wrong/zero, arbitrarily).
   */
  async listFormxSubmissionCounts(orgId: string, projectId: string): Promise<ReportFormxSubmissionCountRowDto[]> {
    return fetchAllPaged<ReportFormxSubmissionCountRowDto>((from, to) =>
      this.supabase
        .from('formx_submissions')
        .select('id, session_id, form_id, session:sessions!inner(project_id)')
        .eq('org_id', orgId)
        .eq('session.project_id', projectId)
        .not('session_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, to),
    );
  }

  /**
   * Latest `p_per_session_limit` (default 5) photo-type formx_values per
   * session, project-wide — server-side aggregation
   * (get_project_formx_photo_preview, 20260810001020), replacing a PostgREST
   * embed query. EXPLAIN ANALYZE on real data showed that query costing 21.5s
   * / ~7M buffer reads, ~99% inside formx_values_select's RLS EXISTS subplan
   * re-evaluated once per value row — same fix as
   * listFormxSubmissionsFullBySession: permission checked once, and the
   * per-session cap is enforced in SQL instead of fetching every photo value
   * project-wide and slicing to 5 in the browser.
   */
  async listFormxPhotoPreviewBySession(
    orgId: string,
    projectId: string,
  ): Promise<Record<string, { submissionId: string; submittedAt: string | null; value: string }[]>> {
    const { data, error } = await this.supabase.rpc('get_project_formx_photo_preview', {
      p_org_id: orgId,
      p_project_id: projectId,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as Record<string, { submissionId: string; submittedAt: string | null; value: string }[]>;
  }

  /**
   * Full nested formx detail (form + every value/field) for one session only.
   * Direct indexed session_id lookup, no join through sessions needed — the
   * lazy fetch behind the report drawer, fired on row click.
   */
  async listFormxSubmissionsForSession(orgId: string, sessionId: string): Promise<ReportFormxSubmissionRowDto[]> {
    const { data, error } = await this.supabase
      .from('formx_submissions')
      .select(FORMX_DETAIL_FIELDS)
      .eq('org_id', orgId)
      .eq('session_id', sessionId)
      .order('submitted_at', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as ReportFormxSubmissionRowDto[];
  }

  /**
   * Full nested formx detail, project-wide, grouped by session_id — the
   * Export/Import modal's on-demand fetch. Server-side aggregation
   * (get_project_formx_export_detail, 20260810001010): the RLS visibility
   * check (own submission OR org owner OR 'manage') is evaluated once inside
   * the function instead of once per formx_values row, and the response is
   * one compact jsonb blob instead of PostgREST's per-row repeated
   * form/field embeds.
   */
  async listFormxSubmissionsFullBySession(
    orgId: string,
    projectId: string,
  ): Promise<Record<string, ReportFormxSubmissionRowDto[]>> {
    const { data, error } = await this.supabase.rpc('get_project_formx_export_detail', {
      p_org_id: orgId,
      p_project_id: projectId,
    });
    if (error) throw new Error(error.message);
    return (data ?? {}) as Record<string, ReportFormxSubmissionRowDto[]>;
  }
}
